import React, { useEffect, useRef, useState } from "react";
import { LearningCycleCard } from "@/components/LearningCycleCard";
import { ModelCodexCard } from "@/components/ModelCodexCard";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import { api } from "@/lib/apiClient";
import { LeagueSelect } from "@/components/LeagueSelect";
import { useAva } from "@/context/AvaContext";
import { NeuralBridge } from "@/components/NeuralBridge";
import { getBrainState } from "@/lib/brainCache";
import { Zap, RotateCcw, Cpu, Loader2, History, Square, Database } from "lucide-react";
import { toast } from "sonner";

const selectCls = "rounded-xl border border-white/10 bg-[#0a0a0a] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50";

export default function SuperBrain() {
  const { speak } = useAva();
  const [leagues, setLeagues] = useState([]);
  const [league, setLeague] = useState(39);
  const [snap, setSnap] = useState(null);
  const [training, setTraining] = useState(false);
  const [log, setLog] = useState([]);
  const [deep, setDeep] = useState(null);
  const [priority, setPriority] = useState([]);
  const [backup, setBackup] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/leagues").then((r) => setLeagues(r.data));
    getBrainState().then((r) => setSnap(r.data));
    refreshDeep();
    loadBackup();
    return () => clearInterval(pollRef.current);
  }, []);

  const refreshDeep = async () => {
    try {
      const r = await api.get("/brain/train/deep/status");
      setDeep(r.data);
      if (r.data.running && !pollRef.current) startPolling();
      if (!r.data.running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch (e) {}
  };

  const startPolling = () => {
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get("/brain/train/deep/status");
        setDeep(r.data);
        getBrainState().then((s) => setSnap(s.data)).catch(() => {});
        if (!r.data.running) { clearInterval(pollRef.current); pollRef.current = null; }
      } catch (e) {}
    }, 3500);
  };

  const startDeep = async (reset = false, prioritize = []) => {
    try {
      const r = await api.post("/brain/train/deep/start", { reset, prioritize });
      setDeep(r.data);
      startPolling();
      if (prioritize.length) {
        toast.success("Reprioritised — OCTON will learn those leagues first");
      } else {
        speak("Diving into the history of the game. I am reading every recorded formation and result, season by season, back to twenty ten. My knowledge bank is growing.");
        toast.success("Deep historical learning started — building the knowledge bank");
      }
    } catch (e) { toast.error("Could not start deep training"); }
  };

  const stopDeep = async () => {
    try {
      const r = await api.post("/brain/train/deep/stop");
      setDeep(r.data);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      toast("Deep training paused — progress saved");
    } catch (e) {}
  };

  const train = async () => {
    setTraining(true);
    try {
      const r = await api.post("/brain/train", { league });
      setSnap(r.data); setLog(r.data.last_matches || []);
      const name = leagues.find((l) => l.id === league)?.name || "the league";
      speak(`Learning complete. I studied ${r.data.learned} real formation outcomes from recent ${name} matches. My synaptic memory now spans ${r.data.episodes} observations, consolidation at ${Math.round(r.data.consolidation * 100)} percent.`);
      toast.success(`Learned from ${r.data.learned} real formations · consolidation ${Math.round(r.data.consolidation * 100)}%`);
    } catch (e) {
      toast.error("Training link interrupted");
    } finally { setTraining(false); }
  };

  const reset = async () => {
    if (!window.confirm("⚠️ WIPE THE BRAIN?\n\nThis erases ALL learned beliefs and the season progress and starts from a newborn brain.\n\nA recoverable backup is saved first, but are you sure? (You almost never need this.)")) return;
    if (!window.confirm("Final check — this really does reset everything. Continue?")) return;
    const r = await api.post("/brain/reset");
    setSnap(r.data); setLog([]); loadBackup();
    toast("Neural core reset to newborn state — use ‘Restore’ to undo");
  };

  const loadBackup = () => api.get("/brain/backup/info").then((r) => setBackup(r.data)).catch(() => {});
  const restore = async () => {
    if (!window.confirm("Restore the last brain snapshot? This brings back the learned beliefs and season progress from before the reset.")) return;
    try {
      const r = await api.post("/brain/restore");
      setSnap(r.data); toast.success("Brain restored — learning recovered");
    } catch { toast.error("No backup available to restore"); }
  };

  const curve = (snap?.reward_curve || []).map((v, i) => ({ i, v }));

  return (
    <div className="px-6 pb-24 pt-20 sm:px-10 lg:px-16">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">NEURAL CORE · REAL-MATCH LEARNING</p>
          <h1 className="font-head mt-2 text-5xl font-black uppercase tracking-tighter">Super Brain</h1>
          <p className="mt-2 max-w-xl text-sm text-white/50">
            OCTON learns from <span className="text-white">real fixtures</span>: it reads the actual formation each team played and the real
            result, then fires Hebbian updates and refines its Bayesian win-probability per formation.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LeagueSelect testId="brain-league-select" value={league} onChange={(id) => setLeague(id)} />
          <button data-testid="train-real-btn" disabled={training} onClick={train}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-black transition hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] disabled:opacity-40">
            {training ? <Loader2 size={15} className="animate-spin" /> : <Cpu size={15} />} {training ? "Learning from real matches…" : "Learn from real matches"}
          </button>
          <button data-testid="reset-brain-btn" disabled={training} onClick={reset} title="Wipe the brain (with confirmation + backup)"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2.5 text-sm text-white/60 hover:text-red-400"><RotateCcw size={15} /></button>
          {backup?.exists && (
            <button data-testid="restore-brain-btn" onClick={restore} title={`Restore last brain (${backup.seasons_done} seasons, ${backup.matches_learned} matches)`}
              className="inline-flex items-center gap-2 rounded-full border border-[#33d17a]/30 px-4 py-2.5 text-sm text-[#33d17a] hover:bg-[#33d17a]/10"><History size={15} /> Restore</button>
          )}
        </div>
      </div>

      <LearningCycleCard />
      <ModelCodexCard />

      {deep && (() => {
        const pct = deep.seasons_total ? Math.round((deep.seasons_done / deep.seasons_total) * 100) : 0;
        const q = deep.quota;
        return (
          <div className="card-tac mb-4 p-5" data-testid="deep-train-panel" data-octon-section="deep dive training history knowledge bank progress" data-octon-label="Deep History Dive">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <History size={18} className="txt-cyan" />
                <div>
                  <p className="font-tac text-[10px] tracking-widest text-white/40">KNOWLEDGE BANK · HISTORY OF THE GAME</p>
                  <p className="text-sm text-white/70">
                    Learn from every recorded formation & result across all leagues, season by season back to 2010.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {deep.running ? (
                  <button data-testid="deep-stop-btn" onClick={stopDeep}
                    className="inline-flex items-center gap-2 rounded-full border border-red-400/30 px-4 py-2.5 text-sm text-red-300 hover:bg-red-400/10">
                    <Square size={14} /> Pause
                  </button>
                ) : (
                  <>
                    <button data-testid="deep-start-btn" onClick={() => startDeep(false)}
                      className="inline-flex items-center gap-2 rounded-full bg-[#ccff00] px-5 py-2.5 text-sm font-semibold text-black transition hover:shadow-[0_0_30px_rgba(204,255,0,0.5)]">
                      <Database size={15} /> {deep.seasons_done > 0 ? "Resume deep dive" : "Begin deep dive"}
                    </button>
                    {deep.seasons_done > 0 && (
                      <button data-testid="deep-reset-btn" onClick={() => { if (window.confirm("Rebuild the whole history from scratch? This wipes the current brain (a backup is saved first).")) startDeep(true); }}
                        className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2.5 text-xs text-white/50 hover:text-red-400">
                        Rebuild from scratch
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-white/5">
              <div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-[#ccff00] transition-all"
                style={{ width: `${pct}%` }} data-testid="deep-progress-bar" />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Seasons", `${deep.seasons_done}/${deep.seasons_total}`, "#00e5ff"],
                ["Matches studied", (deep.matches_learned || 0).toLocaleString(), "#ccff00"],
                ["Clubs in memory", deep.vitals?.teams_known ?? 0, "#ff7300"],
                ["Club×shape beliefs", deep.vitals?.team_pairs ?? 0, "#ffffff"],
              ].map(([k, v, c]) => (
                <div key={k} className="rounded-xl border border-white/8 p-3">
                  <p className="font-mono text-xl font-bold" style={{ color: c }}>{v}</p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40">{k}</p>
                </div>
              ))}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-white/40">
              <span className={`inline-flex items-center gap-1.5 ${deep.running ? "text-cyan-300" : ""}`}>
                <span className={`h-2 w-2 rounded-full ${deep.running ? "animate-pulse bg-cyan-400" : "bg-white/30"}`} />
                {deep.running ? "Learning live" : deep.status === "complete" ? "History fully absorbed" : deep.status === "paused_quota" ? "Paused — daily API quota reached, resume tomorrow" : "Idle"}
              </span>
              {deep.current && <span>· processing {deep.current.replace(":", " · season ")}</span>}
              {q && <span>· API quota {q.current}/{q.limit}</span>}
            </div>

            {deep.message && (
              <p className="mt-2 text-[11px] leading-snug text-white/55" data-testid="deep-status-message">{deep.message}</p>
            )}

            <div className="mt-4 border-t border-white/8 pt-3" data-testid="quota-stretch" data-octon-section="quota stretch prioritise leagues" data-octon-label="Quota Stretch">
              <p className="mb-2 font-tac text-[9px] tracking-widest text-white/40">QUOTA STRETCH · LEARN THESE LEAGUES FIRST</p>
              <div className="flex flex-wrap items-center gap-2">
                {leagues.map((l) => {
                  const on = priority.includes(l.id);
                  return (
                    <button key={l.id} data-testid={`priority-${l.id}`}
                      onClick={() => setPriority((p) => on ? p.filter((x) => x !== l.id) : [...p, l.id])}
                      className={`rounded-full border px-3 py-1 text-[11px] transition ${on ? "border-[#ccff00]/50 bg-[#ccff00]/10 text-[#ccff00]" : "border-white/10 text-white/50 hover:text-white"}`}>
                      {l.name}
                    </button>
                  );
                })}
                <button data-testid="priority-apply" disabled={priority.length === 0}
                  onClick={() => startDeep(false, priority)}
                  className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-cyan-400 px-4 py-1.5 text-[11px] font-semibold text-black disabled:opacity-30">
                  Prioritise
                </button>
              </div>
              <p className="mt-2 text-[10px] text-white/35">Your daily {q ? q.limit : 7500} calls will absorb the selected leagues first when learning resumes.</p>
            </div>
          </div>
        );
      })()}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card-tac relative overflow-hidden p-4 lg:col-span-2">
          {training && <div className="absolute left-0 top-0 h-0.5 w-full tracing-border" />}
          <div className="h-[420px]"><NeuralBridge snapshot={snap} live={training || deep?.running} className="h-full w-full" /></div>
        </div>

        <div className="space-y-4">
          <div className="card-tac p-5">
            <p className="font-tac text-[10px] tracking-widest text-white/40">REWARD CURVE (real results)</p>
            <div className="mt-3 h-32">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={curve}>
                  <YAxis domain={[-1, 1]} hide />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #222", borderRadius: 8, fontSize: 11 }} />
                  <Line type="monotone" dataKey="v" stroke="#ccff00" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card-tac p-5">
            <p className="font-tac text-[10px] tracking-widest text-white/40 mb-3">CORE VITALS</p>
            {[
              ["Observations", snap?.episodes ?? 0, "#00e5ff"],
              ["Consolidation", `${Math.round((snap?.consolidation || 0) * 100)}%`, "#ccff00"],
              ["Avg synapse", snap?.avg_weight ?? 0, "#ff7300"],
              ["Peak synapse", snap?.synapse_strength ?? 0, "#ff3b30"],
              ["Embedding dim", snap?.embedding_dim ?? 512, "#ffffff"],
            ].map(([k, v, c]) => (
              <div key={k} className="flex items-center justify-between border-b border-white/5 py-2 last:border-0">
                <span className="text-sm text-white/50">{k}</span>
                <span className="font-mono text-sm" style={{ color: c }}>{v}</span>
              </div>
            ))}
          </div>

          {log.length > 0 && (
            <div className="card-tac p-5">
              <p className="font-tac text-[10px] tracking-widest text-white/40 mb-2">REAL MATCHES STUDIED</p>
              {log.map((m, i) => (
                <div key={i} className="border-b border-white/5 py-1.5 text-[11px] last:border-0">
                  <p className="text-white/70">{m.match}</p>
                  <p className="font-mono text-white/40">H {m.home_formation || "?"} · A {m.away_formation || "?"}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
